import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { apiPost } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Shield, KeyRound, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import AlertPreferencesCard from "@/components/profile/AlertPreferencesCard";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
};

const ProfilePage = () => {
  const { user, updateProfile, refresh, isAdmin } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    setForm({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      phone: user?.phone || "",
    });
  }, [user]);

  if (!user) return null;

  const role = user.role || "user";

  const onSaveProfile = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First name and last name are required");
      return;
    }
    setBusy(true);
    try {
      await updateProfile({
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
      });
      toast.success("Profile updated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally { setBusy(false); }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setPwBusy(true);
    try {
      await updateProfile({ password: pwForm.next });
      toast.success("Password changed");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setPwBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      {/* Identity card */}
      <Card className="border bg-card">
        <CardHeader className="flex flex-row items-center gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-lg font-semibold">
            {(user.first_name || user.full_name || user.email || "U").charAt(0).toUpperCase()}
          </span>
          <div className="flex-1">
            <CardTitle className="font-display text-lg">{user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "—"}</CardTitle>
            <CardDescription>{user.email}</CardDescription>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="outline" className="capitalize gap-1"><Shield className="h-3 w-3" />{role}</Badge>
              {/* BETA MODE — tier badges hidden for regular users; admin
                  role badge above remains visible. */}
              {user.email_verified && (
                <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                  <MailCheck className="h-3 w-3" /> Verified
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Edit profile */}
      <Card className="border bg-card">
        <CardHeader>
          <CardTitle className="font-display text-base">Personal details</CardTitle>
          <CardDescription>Email is the primary identifier and cannot be changed.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSaveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First name</Label>
              <Input id="first_name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} required minLength={1} data-testid="profile-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last name</Label>
              <Input id="last_name" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} required minLength={1} data-testid="profile-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+94 77 123 4567" data-testid="profile-phone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email (read-only)</Label>
              <Input id="email" value={user.email} disabled className="bg-muted/40" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy} className="gap-2" data-testid="profile-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* BETA MODE — Subscription card replaced with a simple beta access
          notice. PayHere/upgrade UX is hidden during the closed beta. */}
      {!isAdmin && (
        <Card
          className="border bg-[hsl(var(--info))]/5 border-[hsl(var(--info))]/30"
          data-testid="profile-beta-notice"
        >
          <CardHeader>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--info))]" />
              CSE Investor Beta · Full access
            </CardTitle>
            <CardDescription>
              You have full access to every feature for free during our closed beta.
              No payment required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded-md border px-3 py-2">
                <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                  Account created
                </dt>
                <dd className="font-medium">{formatDateTime(user.created_at)}</dd>
              </div>
              <div className="rounded-md border px-3 py-2">
                <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                  Last login
                </dt>
                <dd className="font-medium">{formatDateTime(user.last_login_at)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Admin notice — replaces the subscription card for role=admin */}
      {isAdmin && (
        <Card className="border bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/30" data-testid="profile-admin-notice">
          <CardHeader>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-[hsl(var(--success))]" />
              Admin · Unlimited access
            </CardTitle>
            <CardDescription>
              Admin accounts bypass all subscription tiers, trial periods and quota limits.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Portfolio Alert Preferences (Feature 1) — automatic notifications
          for signal changes / target reached / weekly summary. */}
      <AlertPreferencesCard />

      {/* Change password */}
      <Card className="border bg-card">
        <CardHeader>
          <CardTitle className="font-display text-base flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" /> Change password
          </CardTitle>
          <CardDescription>You'll be signed out of all other devices.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onChangePassword} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="next">New password</Label>
              <Input id="next" type="password" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} minLength={8} required autoComplete="new-password" data-testid="profile-new-password" />
              <p className="text-[11px] text-muted-foreground">Minimum 8 characters.</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} minLength={8} required autoComplete="new-password" data-testid="profile-confirm-password" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={pwBusy} className="gap-2" data-testid="profile-change-password">
                {pwBusy && <Loader2 className="h-4 w-4 animate-spin" />} Change password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
